import { DownOutlined, PhoneOutlined } from '@ant-design/icons';
import { Button, Dropdown, Tooltip, message } from 'antd';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { getCallerIds, initiateCall } from '../../api/telephony/cloudtalk.js';
import { useAuth } from '../../providers/auth';
import { useTelephony } from '../../providers/telephony/telephony-context.js';

const cleanDigits = (s) => String(s || '').replace(/\D/g, '');

// Single-click direct calling: one press dials immediately via the API — the
// call rings in the background CloudTalk phone, no modal opens. The Caller ID
// auto-resolves by the dialed number's market; the "Call from…" submenu offers
// the other Leoprinting lines as a per-call override.
// A contact with TWO different numbers (office + mobile) gets a one-tap
// chooser — each entry dials its own number with its own market Caller ID.
const CallButton = ({
    contact,
    contact_id,
    customer_id,
    project_id,
    code,
    number,
    phone_number,
    mobile_number,
    label,
    size,
    shape,
    type = 'primary',
    style = {},
    showText = undefined,
    onLogged,
}) => {
    const telephony = useTelephony();
    const { expandCloudPhone, noteOutboundAttempt } = telephony || {};
    const { user } = useAuth();
    const hasAgent = telephony?.hasAgent !== undefined ? Boolean(telephony.hasAgent) : Boolean(user?.cloudtalk_agent_id);
    const [calling, setCalling] = useState(false);
    // Other Leoprinting caller lines for the per-call override. Loaded lazily
    // on first right-click so normal pages make zero extra API calls.
    const [callerLines, setCallerLines] = useState(null);

    const loadCallerLines = async () => {
        if (!hasAgent || callerLines) return;
        try {
            const info = await getCallerIds();
            const all = info?.all || {};
            setCallerLines(
                Object.entries(all)
                    .filter(([, num]) => !!num)
                    .map(([market, num]) => ({ key: `line-${market}`, market, number: num })),
            );
        } catch {
            setCallerLines([]);
        }
    };

    // Resolve numbers from props or contact object
    const resolvedPhone = phone_number || number || contact?.phone_number;
    const resolvedMobile = mobile_number || contact?.mobile_number;
    const hasAnyNumber = Boolean(resolvedPhone || resolvedMobile);

    const resolvedName =
        label ||
        contact?.name ||
        [contact?.first_name, contact?.last_name].filter(Boolean).join(' ') ||
        [code, number].filter(Boolean).join(' ') ||
        'Contact';

    const isCircle = shape === 'circle';
    const displayCallText = !isCircle && (showText !== undefined ? showText : true);

    // Distinct dial targets: office phone and mobile, deduplicated. An
    // explicitly pinned number (code+number props) always wins as the single
    // target — the button next to it dials exactly that number.
    const dialTargets = useMemo(() => {
        if (number) {
            return [{ key: 'pinned', type: 'Phone', number, code: code || '' }];
        }
        const list = [];
        const phoneCc = contact?.phone_country_code || code || '';
        const mobileCc = contact?.mobile_country_code || code || '';
        if (resolvedPhone) {
            list.push({ key: 'phone', type: 'Office', number: resolvedPhone, code: phoneCc });
        }
        if (resolvedMobile && cleanDigits(resolvedMobile) !== cleanDigits(resolvedPhone)) {
            list.push({ key: 'mobile', type: 'Mobile', number: resolvedMobile, code: mobileCc });
        }
        return list;
    }, [number, code, resolvedPhone, resolvedMobile, contact, phone_number, mobile_number]);

    const dialDirect = async ({ number: dialNumber, code: dialCode, callerId }) => {
        if (!hasAgent) {
            message.warning('No CloudTalk agent assigned to your account. Please ask an admin to link your seat.');
            return;
        }
        // Auto-expand softphone modal immediately on click so the user sees the call connecting
        expandCloudPhone?.();

        if (calling) return;
        setCalling(true);
        try {
            const result = await initiateCall({
                contact_id: contact?.id || contact_id,
                customer_id: contact?.customer_id || customer_id,
                project_id,
                to_number: dialNumber,
                country_code: dialCode,
                caller_id_number: callerId,
            });
            onLogged?.(result?.call_log);
            // Remember our own dial so the widget labels the agent-leg ring as
            // "answer to connect" instead of an incoming call.
            noteOutboundAttempt?.({ to_number: dialNumber, label: resolvedName });
            message.success(
                result?.dry_run
                    ? 'Test call logged (dry-run)'
                    : `Calling ${resolvedName}${callerId ? ` via ${callerId}` : ''} — press accept in the CloudTalk phone to connect`,
            );
        } catch (err) {
            // No modal fallback: all talking happens in the real CloudTalk
            // phone — expand it so the user can log in and retry from there.
            expandCloudPhone?.();
            message.error(err?.message || 'Could not start call — log into the CloudTalk phone and retry');
        } finally {
            setCalling(false);
        }
    };

    const handleClick = (e) => {
        e.stopPropagation();
        if (!hasAgent) return;
        // Left click always dials instantly with the auto market line.
        // (Right-click opens destination + caller-line options.)
        if (dialTargets.length >= 1) {
            dialDirect(dialTargets[0]);
        }
    };

    const callButton = (
        <Button
            type={type}
            size={size}
            shape={shape}
            style={style}
            icon={<PhoneOutlined />}
            onClick={handleClick}
            loading={calling}
            disabled={!hasAgent || calling}
        >
            {displayCallText ? 'Call' : null}
        </Button>
    );

    if (!hasAnyNumber) return null;

    const menuItems = [
        ...dialTargets.map((t) => ({
            key: t.key,
            label: `${t.type}: ${[t.code, t.number].filter(Boolean).join(' ')}`,
            onClick: () => dialDirect(t),
        })),
    ];
    if (callerLines && callerLines.length > 0) {
        menuItems.push({ type: 'divider' });
        menuItems.push({
            key: 'caller-lines',
            label: 'Call from another number…',
            children: callerLines.map((l) => ({
                key: l.key,
                label: `${l.market} · ${l.number}`,
                onClick: () => dialDirect({ ...dialTargets[0], callerId: l.number }),
            })),
        });
    }

    // Small caret next to the Call button: expands destination numbers and
    // the "call from another line" override. Main button always dials instantly.
    const optionsMenu = (
        <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            disabled={!hasAgent}
            onOpenChange={(open) => {
                if (open) loadCallerLines();
            }}
        >
            <Button
                size="small"
                type="text"
                icon={<DownOutlined style={{ fontSize: 10 }} />}
                onClick={(e) => e.stopPropagation()}
                disabled={!hasAgent}
                style={{ padding: '0 3px', color: 'inherit', lineHeight: 1 }}
                aria-label="Call options"
            />
        </Dropdown>
    );

    const tooltipTitle = !hasAgent
        ? 'No CloudTalk agent assigned to your account'
        : `Call ${resolvedName}`;

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
            <Tooltip title={tooltipTitle}>
                <span style={{ display: 'inline-flex' }}>
                    {callButton}
                </span>
            </Tooltip>
            {hasAgent && (
                <Tooltip title="Call options">
                    {optionsMenu}
                </Tooltip>
            )}
        </span>
    );
};

CallButton.propTypes = {
    contact: PropTypes.object,
    contact_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    customer_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    project_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    code: PropTypes.string,
    number: PropTypes.string,
    phone_number: PropTypes.string,
    mobile_number: PropTypes.string,
    label: PropTypes.string,
    size: PropTypes.string,
    shape: PropTypes.string,
    type: PropTypes.string,
    style: PropTypes.object,
    showText: PropTypes.bool,
    onLogged: PropTypes.func,
};

export default CallButton;
